  import React, { useState, useEffect, useRef, useCallback } from "react";
  import {
    Box,
    Typography,
    Button,
    IconButton,
    Chip,
    Avatar,
    LinearProgress,
    Fade,
    Slide,
    Paper,
  } from "@mui/material";
  import Grid from "@mui/material/Unstable_Grid2";
  import {
    Close as CloseIcon,
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    SmartToy as BotIcon,
    PlayArrow as PlayIcon,
    SkipNext as SkipIcon,
    CheckCircle as CheckIcon,
    Add as AddIcon,
    PersonAdd as PersonAddIcon,
    Group as GroupIcon,
    Search as SearchIcon,
    Chat as ChatIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Help as HelpIcon,
  } from "@mui/icons-material";
  import { alpha, styled, keyframes } from "@mui/material/styles";

  // ─── Keyframes ────────────────────────────────────────────────────────────────
  const pulseRing = keyframes`
    0%   { transform: scale(0.8); opacity: 0.8; }
    50%  { transform: scale(1.2); opacity: 0.4; }
    100% { transform: scale(0.8); opacity: 0.8; }
  `;

  const floatBot = keyframes`
    0%   { transform: translateY(0px); }
    50%  { transform: translateY(-8px); }
    100% { transform: translateY(0px); }
  `;

  const typingDot = keyframes`
    0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
    40%            { transform: scale(1);   opacity: 1;   }
  `;

  const slideInUp = keyframes`
    from { transform: translateY(30px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  `;

  const shimmer = keyframes`
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  `;

  // ─── Styled Components ────────────────────────────────────────────────────────
  const BotAvatar = styled(Avatar)(({ theme }) => ({
    width: 56,
    height: 56,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    boxShadow: "0 4px 20px rgba(102,126,234,0.5)",
    animation: `${floatBot} 3s ease-in-out infinite`,
    cursor: "pointer",
    border: "3px solid white",
    transition: "transform 0.2s",
    "&:hover": { transform: "scale(1.1) translateY(-4px)" },
  }));

  const SpotlightOverlay = styled(Box)({
    position: "fixed",
    inset: 0,
    zIndex: 1200,
    pointerEvents: "none",
  });

  const BotCard = styled(Paper)(({ theme }) => ({
    position: "fixed",
    zIndex: 1400,
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 24px 60px rgba(0,0,0,0.25), 0 8px 20px rgba(102,126,234,0.3)",
    minWidth: 320,
    maxWidth: 380,
    animation: `${slideInUp} 0.4s ease`,
    border: "1px solid rgba(255,255,255,0.2)",
    backdropFilter: "blur(12px)",
  }));

  const ProgressBar = styled(LinearProgress)(({ theme }) => ({
    height: 4,
    borderRadius: 0,
    "& .MuiLinearProgress-bar": {
      background: "linear-gradient(90deg, #667eea, #764ba2)",
    },
    backgroundColor: "rgba(102,126,234,0.15)",
  }));

  const NextButton = styled(Button)(({ theme }) => ({
    borderRadius: 50,
    textTransform: "none",
    fontWeight: 700,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: "8px 24px",
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 20px rgba(102,126,234,0.4)",
    },
    transition: "all 0.2s",
  }));

  const PulseRing = styled(Box)(({ color = "#667eea" }) => ({
    position: "absolute",
    width: 60,
    height: 60,
    borderRadius: "50%",
    border: `3px solid ${color}`,
    animation: `${pulseRing} 1.5s ease-out infinite`,
    pointerEvents: "none",
  }));

  const TypingIndicator = () => (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", py: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            animation: `${typingDot} 1.4s ease-in-out infinite`,
            animationDelay: `${i * 0.16}s`,
          }}
        />
      ))}
    </Box>
  );

  // ─── Tour Steps ───────────────────────────────────────────────────────────────
  const TOUR_STEPS = [
    {
      id: "welcome",
      targetId: null,
      icon: <BotIcon />,
      title: "👋 Welcome to Team Workspace!",
      description:
        "Hi there! I'm your Team Guide Bot. Let me walk you through all the powerful features available in your Team Workspace. This will only take a minute!",
      color: "#667eea",
      position: "center",
      highlight: null,
    },
    {
      id: "search",
      targetId: "tour-search",
      icon: <SearchIcon />,
      title: "🔍 Search Bar",
      description:
        "Use this search bar to instantly find team members, groups, or shared groups by name. It filters all sections in real-time as you type!",
      color: "#4CAF50",
      position: "bottom",
      highlight: "Search for team members or groups",
    },
    {
      id: "create-group",
      targetId: "tour-create-group",
      icon: <AddIcon />,
      title: "➕ Create Group",
      description:
        "Click 'Create Group' to organize your team members into custom groups. Groups enable group chats, shared resources, and better collaboration!",
      color: "#667eea",
      position: "bottom",
      highlight: "Organize your team with groups",
    },
    {
      id: "invite",
      targetId: "tour-invite",
      icon: <PersonAddIcon />,
      title: "📧 Invite Team Members",
      description:
        "Use 'Invite' to send email invitations to new team members. You can add multiple emails at once — perfect for onboarding an entire team!",
      color: "#9C27B0",
      position: "bottom",
      highlight: "Invite multiple members via email",
    },
    {
      id: "team-members",
      targetId: "tour-team-members",
      icon: <GroupIcon />,
      title: "👥 Team Members Section",
      description:
        "All your team members appear here as cards. Each card shows their name, role, and quick action buttons to chat with them or remove them.",
      color: "#2196F3",
      position: "top",
      highlight: "View all team members",
    },
    {
      id: "member-chat",
      targetId: "tour-member-chat",
      icon: <ChatIcon />,
      title: "💬 Direct Chat",
      description:
        "Click the Chat button on any member card to open a direct conversation with them instantly. Stay connected with your teammates!",
      color: "#00BCD4",
      position: "top",
      highlight: "Start a direct conversation",
    },
    {
      id: "member-delete",
      targetId: "tour-member-delete",
      icon: <DeleteIcon />,
      title: "🗑️ Remove Member",
      description:
        "The delete button lets admins remove team members from the workspace. A confirmation dialog will appear to prevent accidental removals.",
      color: "#f44336",
      position: "top",
      highlight: "Remove members safely",
    },
    {
      id: "my-groups",
      targetId: "tour-my-groups",
      icon: <GroupIcon />,
      title: "🏠 My Groups",
      description:
        "Groups you created appear here. You can see member counts, open group chat, manage members, or delete groups you own.",
      color: "#667eea",
      position: "top",
      highlight: "Manage groups you own",
    },
    {
      id: "group-chat",
      targetId: "tour-group-chat",
      icon: <ChatIcon />,
      title: "💬 Group Chat",
      description:
        "Open a group chat channel for any of your groups. The badge shows unread message count. All group members can communicate in real-time!",
      color: "#00BCD4",
      position: "top",
      highlight: "Group communication hub",
    },
    {
      id: "group-manage",
      targetId: "tour-group-manage",
      icon: <ViewIcon />,
      title: "⚙️ Manage Group Members",
      description:
        "Click the members icon to open the group management panel. You can add available team members or remove existing ones from the group.",
      color: "#FF9800",
      position: "top",
      highlight: "Add or remove group members",
    },
    {
      id: "shared-groups",
      targetId: "tour-shared-groups",
      icon: <GroupIcon />,
      title: "🤝 Shared Groups",
      description:
        "Groups created by others that you've been added to appear here. You can view all members and leave a shared group whenever you want.",
      color: "#FF9800",
      position: "top",
      highlight: "Groups shared with you",
    },
    {
      id: "help",
      targetId: "tour-help",
      icon: <HelpIcon />,
      title: "❓ Help Tour",
      description:
        "Need a refresher? Click the Help button anytime to restart this guided tour. It's always here for you!",
      color: "#607D8B",
      position: "bottom",
      highlight: "Restart this guide anytime",
    },
    {
      id: "done",
      targetId: null,
      icon: <CheckIcon />,
      title: "🎉 You're All Set!",
      description:
        "You've completed the Team Workspace tour! You're ready to collaborate, create groups, invite members, and manage your team like a pro. Happy teaming!",
      color: "#4CAF50",
      position: "center",
      highlight: null,
    },
  ];

  // ─── Main Component ───────────────────────────────────────────────────────────
  export default function TeamFeatureBot({
    show = true,
    autoStart = false,
    onAutoStart,
  }) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [displayedText, setDisplayedText] = useState("");
    const [targetRect, setTargetRect] = useState(null);
    const [cardPos, setCardPos] = useState({ top: "50%", left: "50%" });
    const [isVisible, setIsVisible] = useState(false);
    const typingRef = useRef(null);

    const step = TOUR_STEPS[currentStep];
    const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

    // ── Find Target Element ──
    const findTarget = useCallback((targetId) => {
      if (!targetId) return null;
      return document.getElementById(targetId);
    }, []);

    // ── Scroll & Position ──
    const positionCard = useCallback((el, position) => {
      if (!el) {
        setTargetRect(null);
        setCardPos({
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        });
        return;
      }

      el.scrollIntoView({ behavior: "smooth", block: "center" });

      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setTargetRect(rect);

        const cardW = 380;
        const cardH = 260;
        const margin = 16;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let top, left;

        if (position === "bottom") {
          top = rect.bottom + margin;
          left = rect.left + rect.width / 2 - cardW / 2;
        } else if (position === "top") {
          top = rect.top - cardH - margin;
          left = rect.left + rect.width / 2 - cardW / 2;
        } else {
          top = rect.right + margin;
          left = rect.top;
        }

        // clamp
        left = Math.max(margin, Math.min(left, vw - cardW - margin));
        top = Math.max(margin, Math.min(top, vh - cardH - margin));

        setCardPos({ top, left, transform: "none" });
      }, 400);
    }, []);

    // ── Typing Animation ──
    const typeText = useCallback((text) => {
      setIsTyping(true);
      setDisplayedText("");
      let i = 0;
      clearInterval(typingRef.current);
      typingRef.current = setInterval(() => {
        if (i < text.length) {
          setDisplayedText(text.slice(0, i + 1));
          i++;
        } else {
          clearInterval(typingRef.current);
          setIsTyping(false);
        }
      }, 18);
    }, []);

    // ── Advance to Step ──
    const goToStep = useCallback(
      (idx) => {
        const s = TOUR_STEPS[idx];
        setCurrentStep(idx);
        const el = findTarget(s.targetId);
        positionCard(el, s.position);
        setIsVisible(false);
        setTimeout(() => {
          setIsVisible(true);
          typeText(s.description);
        }, 300);
      },
      [findTarget, positionCard, typeText]
    );

    const startTour = () => {
      setIsOpen(true);
      goToStep(0);
    };

    const closeTour = () => {
      setIsOpen(false);
      setCurrentStep(0);
      setTargetRect(null);
      clearInterval(typingRef.current);
    };

    const next = () => {
      if (currentStep < TOUR_STEPS.length - 1) goToStep(currentStep + 1);
      else closeTour();
    };

    const prev = () => {
      if (currentStep > 0) goToStep(currentStep - 1);
    };

    useEffect(() => () => clearInterval(typingRef.current), []);

    useEffect(() => {
      if (show && autoStart && !isOpen) {
        startTour();
        if (typeof onAutoStart === "function") {
          onAutoStart();
        }
      }
    }, [show, autoStart, isOpen, onAutoStart]);

    if (!show) return null;

    return (
      <>
        {/* ── Floating Bot Launcher ── */}
        {!isOpen && (
          <Box
            sx={{
              position: "fixed",
              bottom: 95,
              right: 32,
              zIndex: 1300,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Fade in>
              <Box sx={{ position: "relative" }}>
                <PulseRing
                  color="#667eea"
                  sx={{ top: "-2px", left: "-2px", width: 60, height: 60 }}
                />
                <BotAvatar onClick={startTour}>
                  <BotIcon sx={{ fontSize: 40 }} />
                </BotAvatar>
              </Box>
            </Fade>
            <Paper
              elevation={4}
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 50,
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                cursor: "pointer",
                animation: `${shimmer} 3s linear infinite`,
                backgroundSize: "200% auto",
              }}
              onClick={startTour}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="white"
                noWrap
              >
                ✨ Take a Tour
              </Typography>
            </Paper>
          </Box>
        )}

        {/* ── Spotlight Overlay ── */}
        {isOpen && (
          <SpotlightOverlay>
            <svg
              width="100%"
              height="100%"
              style={{ position: "absolute", inset: 0 }}
            >
              <defs>
                <mask id="spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  {targetRect && (
                    <rect
                      x={targetRect.left - 8}
                      y={targetRect.top - 8}
                      width={targetRect.width + 16}
                      height={targetRect.height + 16}
                      rx={12}
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(0,0,0,0.55)"
                mask="url(#spotlight-mask)"
              />
              {targetRect && (
                <>
                  <rect
                    x={targetRect.left - 8}
                    y={targetRect.top - 8}
                    width={targetRect.width + 16}
                    height={targetRect.height + 16}
                    rx={12}
                    fill="none"
                    stroke={step.color}
                    strokeWidth={3}
                    opacity={0.9}
                  />
                  {/* animated corner brackets */}
                  {[
                    [targetRect.left - 10, targetRect.top - 10],
                    [targetRect.right - 20, targetRect.top - 10],
                    [targetRect.left - 10, targetRect.bottom - 20],
                    [targetRect.right - 20, targetRect.bottom - 20],
                  ].map(([cx, cy], i) => (
                    <g key={i} transform={`translate(${cx},${cy})`}>
                      <rect width={20} height={20} rx={4} fill={step.color} opacity={0.25} />
                    </g>
                  ))}
                </>
              )}
            </svg>
          </SpotlightOverlay>
        )}

        {/* ── Bot Card ── */}
        {isOpen && (
          <BotCard
            style={{
              top: typeof cardPos.top === "number" ? cardPos.top : cardPos.top,
              left:
                typeof cardPos.left === "number" ? cardPos.left : cardPos.left,
              transform: cardPos.transform,
            }}
          >
            {/* Progress bar */}
            <ProgressBar variant="determinate" value={progress} />

            {/* Header */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${step.color}dd, ${step.color}99)`,
                px: 2.5,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  background: "rgba(255,255,255,0.2)",
                  border: "2px solid rgba(255,255,255,0.4)",
                  animation: `${floatBot} 3s ease-in-out infinite`,
                }}
              >
                <BotIcon sx={{ fontSize: 20, color: "white" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontWeight: 600,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    fontSize: 10,
                  }}
                >
                  Team Guide Bot
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="white"
                  lineHeight={1.2}
                >
                  {step.title}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={`${currentStep + 1}/${TOUR_STEPS.length}`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 10,
                    height: 22,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={closeTour}
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.15)",
                    "&:hover": { bgcolor: "rgba(255,68,68,0.6)" },
                    width: 28,
                    height: 28,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Body */}
            <Box
              sx={{
                px: 2.5,
                py: 2,
                bgcolor: "white",
                minHeight: 90,
              }}
            >
              {step.highlight && (
                <Chip
                  label={step.highlight}
                  size="small"
                  sx={{
                    mb: 1.5,
                    bgcolor: alpha(step.color, 0.1),
                    color: step.color,
                    fontWeight: 600,
                    fontSize: 11,
                    border: `1px solid ${alpha(step.color, 0.3)}`,
                  }}
                />
              )}
              <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                {displayedText}
                {isTyping && (
                  <Box component="span" sx={{ display: "inline-block", ml: 0.5 }}>
                    <TypingIndicator />
                  </Box>
                )}
              </Typography>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: "#f8f9ff",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                {currentStep > 0 && (
                  <IconButton
                    size="small"
                    onClick={prev}
                    sx={{
                      border: "1px solid rgba(102,126,234,0.3)",
                      color: "#667eea",
                      "&:hover": { bgcolor: alpha("#667eea", 0.08) },
                      width: 32,
                      height: 32,
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                )}
                <Button
                  size="small"
                  startIcon={<SkipIcon fontSize="small" />}
                  onClick={closeTour}
                  sx={{
                    textTransform: "none",
                    color: "text.secondary",
                    fontSize: 12,
                    "&:hover": { color: "error.main" },
                  }}
                >
                  Skip tour
                </Button>
              </Box>

              <NextButton size="small" onClick={next} endIcon={<ArrowForwardIcon />}>
                {currentStep === TOUR_STEPS.length - 1 ? "Finish 🎉" : "Next"}
              </NextButton>
            </Box>
          </BotCard>
        )}
      </>
    );
  }
